import React, {
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ImageResize } from "quill-image-resize-module-ts";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../../../../../DefaultStyles/Loader";
import { useApi } from "../../../../../../hooks/useApi";
import { serverUrl } from "../../../../../../AdminConfig";

// 커스텀 TableBlot - 테이블을 하나의 블록으로 처리
const BlockEmbed = Quill.import("blots/block/embed") as any;

class TableBlot extends BlockEmbed {
  static create(value: string) {
    const node = super.create() as HTMLElement;
    node.innerHTML = value;
    node.setAttribute("contenteditable", "false");
    node.classList.add("ql-custom-table");
    return node;
  }

  static value(node: HTMLElement) {
    return node.innerHTML;
  }
}
TableBlot.blotName = "customTable";
TableBlot.tagName = "DIV";
TableBlot.className = "ql-custom-table";

Quill.register(TableBlot, true);
Quill.register("modules/imageResize", ImageResize);

// Size whitelist 설정 - 붙여넣기 시 size 클래스 유지를 위해 필요
const Size = Quill.import("attributors/class/size") as any;
Size.whitelist = ["small", "large", "huge"];
Quill.register(Size, true);

interface FaqContentInputProps {
  contents: string;
  setContents: Dispatch<SetStateAction<string>>;
  editMode: boolean;
  initContent: string;
}

interface SContent {
  savedContent?: string;
}

export default function FaqContentInput({
  contents,
  setContents,
  editMode,
  initContent,
}: FaqContentInputProps) {
  const QuillRef = useRef<ReactQuill>();
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [initImgSrc, setInitImgSrc] = useState<string[]>([]);
  const [hasSameImgSrc, setHasSameImgSrc] = useState(false);
  const [deleteFile, setDeleteFile] = useState(false);
  const [deleteFileKey, setDeleteFileKey] = useState("");
  const [deleteFileCnt, setDeleteFileCnt] = useState(0);
  
  // 테이블 컨텍스트 메뉴 상태
  const [tableContextMenu, setTableContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    targetCell: HTMLTableCellElement | null;
  }>({ visible: false, x: 0, y: 0, targetCell: null });

  // 리사이즈 상태
  const [resizing, setResizing] = useState<{
    active: boolean;
    type: 'col' | 'table' | null;
    startX: number;
    startWidth: number;
    target: HTMLElement | null;
    colIndex?: number;
  }>({ active: false, type: null, startX: 0, startWidth: 0, target: null });

  const formats = [
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'size', 'color',
  'list', 'bullet', 'indent', 'align',
  'link', 'image',
  'customTable'
];

  console.log("✅✅✅ hasSameImgSrc ? ", hasSameImgSrc);
  console.log(deleteFile, deleteFileKey);

  // 이미지 삭제하기
  const { response, loading, error } = useApi({
    method: "post",
    url:
      !hasSameImgSrc && deleteFile && deleteFileKey !== ""
        ? `file/delete`
        : undefined,
    data: {
      key: deleteFileKey,
    },
  });

  // 이미지 추가하기
  const imageHandler = () => {
    console.log("에디터에서 이미지 버튼을 클릭하면 이 핸들러가 시작됩니다!");

    // 1. 이미지를 저장할 input type=file DOM을 만든다.
    const input = document.createElement("input");
    // 속성 써주기
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click(); // 에디터 이미지버튼을 클릭하면 이 input이 클릭된다.
    // input이 클릭되면 파일 선택창이 나타난다.

    // input에 변화가 생긴다면 = 이미지를 선택
    input.addEventListener("change", async () => {
      console.log("온체인지");
      if (input?.files && input?.files[0]) {
        const file = input.files[0];
        // multer에 맞는 형식으로 데이터 만들어준다.

        console.log(file);

        const formData = new FormData();
        formData.append("file", file); // formData는 키-밸류 구조
        // 백엔드 multer라우터에 이미지를 보낸다.

        try {
          setIsLoading(true);

          const result = await axios.post(`${serverUrl}/file`, formData);

          console.log(result);

          if (result?.status === 201) {
            setIsLoading(false);
            console.log("성공");

            const IMG_URL = `${result?.data}` ?? "";
            // 이 URL을 img 태그의 src에 넣은 요소를 현재 에디터의 커서에 넣어주면 에디터 내에서 이미지가 나타난다
            // src가 base64가 아닌 짧은 URL이기 때문에 데이터베이스에 에디터의 전체 글 내용을 저장할 수있게된다
            // 이미지는 꼭 로컬 백엔드 uploads 폴더가 아닌 다른 곳에 저장해 URL로 사용하면된다.

            // 이미지 태그를 에디터에 써주기 - 여러 방법이 있다.
            const editor = QuillRef?.current?.getEditor(); // 에디터 객체 가져오기
            // 1. 에디터 root의 innerHTML을 수정해주기

            if (editor) {
              // 2. 현재 에디터 커서 위치값을 가져온다
              const range = editor?.getSelection();
              // 가져온 위치에 이미지를 삽입한다
              editor.insertEmbed(range?.index ?? 0, "image", IMG_URL);
            }
          } else if (result?.status === 413) {
            setIsLoading(false);

            toast.error(
              "이미지 용량이 5MB를 초과하였습니다. 다른 이미지로 다시 시도해주세요."
            );
          } else {
            setIsLoading(false);

            toast.error(
              result?.data ??
                `이미지 업로드에 실패하였습니다. 에러 코드: ${
                  result?.status ?? "0"
                }`
            );
          }
        } catch (error) {
          setIsLoading(false);

          console.log(error);
          toast.error("이미지 업로드에 실패하였습니다.");
        }
      }
    });
  };

  // 테이블 HTML 생성 함수
  const generateTableHTML = (rows: number, cols: number, width: string = '100%') => {
    let html = `<table style="border-collapse: collapse; width: ${width}; margin: 10px 0;"><tbody>`;
    for (let i = 0; i < rows; i++) {
      html += '<tr>';
      for (let j = 0; j < cols; j++) {
        html += '<td style="border: 1px solid #ccc; padding: 8px; min-width: 50px;" contenteditable="true">&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    return html;
  };

  // 테이블 삽입 핸들러 (기본 3x3, 100% 너비로 바로 생성)
  const tableHandler = () => {
    const editor = QuillRef?.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const index = range?.index ?? editor.getLength();
      
      // 기본 3x3, 100% 너비 테이블 생성
      const tableHTML = generateTableHTML(3, 3, '100%');
      
      // customTable blot으로 삽입
      editor.insertEmbed(index, 'customTable', tableHTML, 'user');
      editor.setSelection(index + 1, 0);
    }
  };

  // 테이블 우클릭 핸들러
  const handleTableContextMenu = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const cell = target.closest('td') as HTMLTableCellElement;
    
    if (cell && target.closest('.ql-custom-table')) {
      e.preventDefault();
      setTableContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        targetCell: cell,
      });
    }
  };

  // 메뉴 위치 계산 (화면 밖으로 나가지 않도록)
  const getMenuPosition = () => {
    const menuHeight = 500; // 예상 메뉴 높이
    const menuWidth = 180;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    let x = tableContextMenu.x;
    let y = tableContextMenu.y;
    
    // 화면 하단을 벗어나면 위로 표시
    if (y + menuHeight > windowHeight) {
      y = Math.max(10, windowHeight - menuHeight - 10);
    }
    
    // 화면 우측을 벗어나면 왼쪽으로
    if (x + menuWidth > windowWidth) {
      x = windowWidth - menuWidth - 10;
    }
    
    return { x, y };
  };

  // 컨텍스트 메뉴 닫기
  const closeContextMenu = () => {
    setTableContextMenu({ visible: false, x: 0, y: 0, targetCell: null });
  };

  // 행 위에 추가
  const addRowAbove = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const row = cell.closest('tr');
    if (!row) return;
    
    const colCount = row.cells.length;
    const newRow = document.createElement('tr');
    for (let i = 0; i < colCount; i++) {
      const td = document.createElement('td');
      td.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; min-width: 50px;');
      td.setAttribute('contenteditable', 'true');
      td.innerHTML = '&nbsp;';
      newRow.appendChild(td);
    }
    row.parentNode?.insertBefore(newRow, row);
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 행 아래에 추가
  const addRowBelow = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const row = cell.closest('tr');
    if (!row) return;
    
    const colCount = row.cells.length;
    const newRow = document.createElement('tr');
    for (let i = 0; i < colCount; i++) {
      const td = document.createElement('td');
      td.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; min-width: 50px;');
      td.setAttribute('contenteditable', 'true');
      td.innerHTML = '&nbsp;';
      newRow.appendChild(td);
    }
    row.parentNode?.insertBefore(newRow, row.nextSibling);
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 열 왼쪽에 추가
  const addColLeft = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const cellIndex = cell.cellIndex;
    const table = cell.closest('table');
    if (!table) return;
    
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const td = document.createElement('td');
      td.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; min-width: 50px;');
      td.setAttribute('contenteditable', 'true');
      td.innerHTML = '&nbsp;';
      row.insertBefore(td, row.cells[cellIndex]);
    });
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 열 오른쪽에 추가
  const addColRight = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const cellIndex = cell.cellIndex;
    const table = cell.closest('table');
    if (!table) return;
    
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const td = document.createElement('td');
      td.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; min-width: 50px;');
      td.setAttribute('contenteditable', 'true');
      td.innerHTML = '&nbsp;';
      row.insertBefore(td, row.cells[cellIndex + 1] || null);
    });
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 행 삭제
  const deleteRow = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const row = cell.closest('tr');
    const table = cell.closest('table');
    if (!row || !table) return;
    
    const rowCount = table.querySelectorAll('tr').length;
    if (rowCount <= 1) {
      toast.error("마지막 행은 삭제할 수 없습니다.");
      closeContextMenu();
      return;
    }
    
    row.remove();
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 열 삭제
  const deleteCol = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const cellIndex = cell.cellIndex;
    const table = cell.closest('table');
    if (!table) return;
    
    const firstRow = table.querySelector('tr');
    if (!firstRow || firstRow.cells.length <= 1) {
      toast.error("마지막 열은 삭제할 수 없습니다.");
      closeContextMenu();
      return;
    }
    
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      if (row.cells[cellIndex]) {
        row.cells[cellIndex].remove();
      }
    });
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 테이블 삭제
  const deleteTable = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const tableWrapper = cell.closest('.ql-custom-table');
    if (tableWrapper) {
      tableWrapper.remove();
      updateContentsFromEditor();
    }
    closeContextMenu();
  };

  // 테이블 너비 조절
  const changeTableWidth = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const table = cell.closest('table') as HTMLTableElement;
    if (!table) return;
    
    const currentWidth = table.style.width || '100%';
    const widthInput = prompt(
      "테이블 너비를 입력하세요\n(예: 100%, 500px, 50%)",
      currentWidth
    );
    
    if (!widthInput) {
      closeContextMenu();
      return;
    }
    
    table.style.width = widthInput;
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 열 너비 조절
  const changeColWidth = () => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const currentWidth = cell.style.width || 'auto';
    const widthInput = prompt(
      "열 너비를 입력하세요\n(예: 100px, 20%)",
      currentWidth === 'auto' ? '' : currentWidth
    );
    
    if (!widthInput) {
      closeContextMenu();
      return;
    }
    
    const cellIndex = cell.cellIndex;
    const table = cell.closest('table');
    if (!table) return;
    
    // 같은 열의 모든 셀 너비 변경
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const targetCell = row.cells[cellIndex] as HTMLTableCellElement;
      if (targetCell) {
        targetCell.style.width = widthInput;
      }
    });
    
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 셀 배경색 변경
  const changeCellBgColor = (color: string) => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    cell.style.backgroundColor = color;
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 행 배경색 변경
  const changeRowBgColor = (color: string) => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const row = cell.closest('tr');
    if (!row) return;
    
    Array.from(row.cells).forEach((c) => {
      (c as HTMLTableCellElement).style.backgroundColor = color;
    });
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 열 배경색 변경
  const changeColBgColor = (color: string) => {
    const cell = tableContextMenu.targetCell;
    if (!cell) return;
    
    const cellIndex = cell.cellIndex;
    const table = cell.closest('table');
    if (!table) return;
    
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const targetCell = row.cells[cellIndex] as HTMLTableCellElement;
      if (targetCell) {
        targetCell.style.backgroundColor = color;
      }
    });
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 선택된 텍스트를 HTML 태그로 감싸는 헬퍼 함수
  const wrapSelectionWithTag = (tagName: string, style?: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      alert('텍스트를 먼저 선택해주세요.');
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    if (!selectedText) {
      alert('텍스트를 먼저 선택해주세요.');
      return;
    }

    // 새 요소 생성
    const wrapper = document.createElement(tagName);
    if (style) {
      wrapper.setAttribute('style', style);
    }
    
    // 선택 영역의 내용을 새 요소로 감싸기
    try {
      range.surroundContents(wrapper);
    } catch (e) {
      // 선택 영역이 여러 요소에 걸쳐있는 경우
      const fragment = range.extractContents();
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);
    }
    
    // 선택 해제
    selection.removeAllRanges();
    
    updateContentsFromEditor();
    closeContextMenu();
  };

  // 셀 내 텍스트 굵게
  const toggleCellBold = () => {
    wrapSelectionWithTag('strong');
  };

  // 셀 내 텍스트 기울임
  const toggleCellItalic = () => {
    wrapSelectionWithTag('em');
  };

  // 셀 내 텍스트 밑줄
  const toggleCellUnderline = () => {
    wrapSelectionWithTag('u');
  };

  // 셀 내 텍스트 취소선
  const toggleCellStrikethrough = () => {
    wrapSelectionWithTag('s');
  };

  // 셀 내 텍스트 색상 변경
  const changeCellTextColor = (color: string) => {
    wrapSelectionWithTag('span', `color: ${color};`);
  };

  // 에디터 내용을 contents state에 반영
  const updateContentsFromEditor = () => {
    const editor = QuillRef?.current?.getEditor();
    if (editor) {
      const html = editor.root.innerHTML;
      setContents(html);
    }
  };

  // 클릭 시 컨텍스트 메뉴 닫기
  useEffect(() => {
    const handleClick = () => closeContextMenu();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // 테이블 리사이즈 핸들 추가
  useEffect(() => {
    if (!editMode) return;
    
    const container = editorContainerRef.current;
    if (!container) return;

    const addResizeHandles = () => {
      // 기존 핸들 제거
      container.querySelectorAll('.table-resize-handle, .col-resize-handle').forEach(el => el.remove());
      
      const tables = container.querySelectorAll('.ql-custom-table table');
      tables.forEach((table) => {
        const tableEl = table as HTMLTableElement;
        const wrapper = tableEl.closest('.ql-custom-table') as HTMLElement;
        if (!wrapper) return;
        
        wrapper.style.position = 'relative';
        wrapper.style.display = 'inline-block';
        
        // 테이블 우측 리사이즈 핸들
        const tableHandle = document.createElement('div');
        tableHandle.className = 'table-resize-handle';
        tableHandle.style.cssText = `
          position: absolute;
          right: -4px;
          top: 0;
          width: 8px;
          height: 100%;
          cursor: ew-resize;
          background: transparent;
          z-index: 10;
        `;
        tableHandle.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setResizing({
            active: true,
            type: 'table',
            startX: e.clientX,
            startWidth: tableEl.offsetWidth,
            target: tableEl,
          });
        });
        wrapper.appendChild(tableHandle);
        
        // 열 리사이즈 핸들 (각 셀의 우측 경계)
        const firstRow = tableEl.querySelector('tr');
        if (firstRow) {
          Array.from(firstRow.cells).forEach((cell, index) => {
            if (index === firstRow.cells.length - 1) return; // 마지막 열은 제외
            
            const colHandle = document.createElement('div');
            colHandle.className = 'col-resize-handle';
            colHandle.dataset.colIndex = String(index);
            
            const cellRect = cell.getBoundingClientRect();
            const wrapperRect = wrapper.getBoundingClientRect();
            
            colHandle.style.cssText = `
              position: absolute;
              left: ${cellRect.right - wrapperRect.left - 4}px;
              top: 0;
              width: 8px;
              height: 100%;
              cursor: col-resize;
              background: transparent;
              z-index: 10;
            `;
            
            colHandle.addEventListener('mousedown', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setResizing({
                active: true,
                type: 'col',
                startX: e.clientX,
                startWidth: cell.offsetWidth,
                target: tableEl,
                colIndex: index,
              });
            });
            wrapper.appendChild(colHandle);
          });
        }
      });
    };

    // MutationObserver로 테이블 변경 감지
    const observer = new MutationObserver(() => {
      setTimeout(addResizeHandles, 100);
    });
    
    observer.observe(container, { childList: true, subtree: true });
    
    // 초기 실행
    setTimeout(addResizeHandles, 500);
    
    return () => observer.disconnect();
  }, [editMode, contents]);

  // 마우스 이동/업 이벤트 핸들러
  useEffect(() => {
    if (!resizing.active) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing.active || !resizing.target) return;
      
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff);
      
      if (resizing.type === 'table') {
        resizing.target.style.width = `${newWidth}px`;
      } else if (resizing.type === 'col' && resizing.colIndex !== undefined) {
        const table = resizing.target as HTMLTableElement;
        const rows = table.querySelectorAll('tr');
        rows.forEach((row) => {
          const cell = row.cells[resizing.colIndex!];
          if (cell) {
            cell.style.width = `${newWidth}px`;
          }
        });
      }
    };

    const handleMouseUp = () => {
      if (resizing.active) {
        updateContentsFromEditor();
        setResizing({ active: false, type: null, startX: 0, startWidth: 0, target: null });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { size: ["small", false, "large", "huge"] },
            { color: ["#2f2f38", "#f42d16", "#00c7c7", "#8b8b8b"] },
          ],
          [
            // { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
            { align: [] },
          ],
          ["link", "image", "table"],
        ],

        handlers: {
          image: imageHandler,
          table: tableHandler,
        },
      },
      clipboard: {
        matchVisual: false,
        matchers: [
          [
            Node.ELEMENT_NODE,
            (node: HTMLElement, delta: any) => {
              // ql-size 클래스가 있는 경우 size 속성 유지
              if (node.classList) {
                if (node.classList.contains("ql-size-small")) {
                  delta.ops = delta.ops.map((op: any) => {
                    if (op.insert && typeof op.insert === "string") {
                      return {
                        ...op,
                        attributes: { ...op.attributes, size: "small" },
                      };
                    }
                    return op;
                  });
                } else if (node.classList.contains("ql-size-large")) {
                  delta.ops = delta.ops.map((op: any) => {
                    if (op.insert && typeof op.insert === "string") {
                      return {
                        ...op,
                        attributes: { ...op.attributes, size: "large" },
                      };
                    }
                    return op;
                  });
                } else if (node.classList.contains("ql-size-huge")) {
                  delta.ops = delta.ops.map((op: any) => {
                    if (op.insert && typeof op.insert === "string") {
                      return {
                        ...op,
                        attributes: { ...op.attributes, size: "huge" },
                      };
                    }
                    return op;
                  });
                }
              }
              return delta;
            },
          ],
        ],
      },
      imageResize: {
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
    }),
    []
  );

  const markup = () => {
    if (contents !== "") {
      return { __html: contents?.replaceAll("&amp;", "&") };
    }
  };

  // 초기 이미지 url 추출
  useEffect(() => {
    if (initContent !== "") {
      let initImgSrcList: string[] = [];

      const imgReg = /(src="(.*?)")/g;
      const initImgSrc = initContent?.match(imgReg);

      console.log("initImgSrc", initImgSrc);

      if (initImgSrc) {
        const src = initImgSrc?.map((a) => a.match(/([^"]*)\w+/gm));

        src.forEach((el) => {
          const srcUrl: string | undefined = el?.[1];

          initImgSrcList.push(srcUrl!);
        });

        console.log("initImgSrcList", initImgSrcList);

        setInitImgSrc(initImgSrcList);
      }
    }
  }, [initContent]);

  // 자주 묻는 질문 수정시 이미지가 변경 되었는지 체크
  useEffect(() => {
    if (initImgSrc?.length > 0 && contents !== "") {
      let contentImgSrcList: string[] = [];

      const imgReg = /(src="(.*?)")/g;
      const contentImgSrc = contents?.match(imgReg);

      console.log("init", contentImgSrc);

      if (contentImgSrc) {
        const src = contentImgSrc?.map((a) => a.match(/([^"]*)\w+/gm));

        src.forEach((el) => {
          const srcUrl: string | undefined = el?.[1];

          contentImgSrcList.push(srcUrl!);
        });

        console.log("contentImgSrcList", contentImgSrcList);

        const isSameSrc = initImgSrc.every((item) =>
          contentImgSrcList.includes(item)
        );

        console.log("initImgSrc", isSameSrc, initImgSrc, contentImgSrcList);

        const notIncludeInitSrc = initImgSrc?.filter(
          (i) => !contentImgSrcList.includes(i)
        );

        console.log("notIncludeInitSrc", notIncludeInitSrc);

        if (notIncludeInitSrc?.length > 0) {
          setDeleteFileKey(
            notIncludeInitSrc[deleteFileCnt]?.split(
              "https://linkidimage.s3.ap-northeast-2.amazonaws.com/"
            )[1] ??
              notIncludeInitSrc[deleteFileCnt]?.split(
                "https://storage.googleapis.com/linkidimage/"
              )[1]
          );
          setDeleteFile(true);
        }

        if (isSameSrc) {
          setHasSameImgSrc(true);
        } else {
          setHasSameImgSrc(false);
        }
      }
    }
  }, [initImgSrc, contents]);

  useEffect(() => {
    if (response?.status === 200) {
      toast.success("사진 삭제에 성공하였습니다.");
      setDeleteFile(false);
      setDeleteFileKey("");
      setDeleteFileCnt((prev) => prev + 1);
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.error(error?.data ?? "사진 삭제에 실패하였습니다.");
    }
  }, [error]);

  return (
    <>
      {editMode ? (
        <EditModeContent 
          ref={editorContainerRef}
          style={{ padding: 0 }} 
          onContextMenu={handleTableContextMenu}
        >
          <SQuill
            ref={(element) => {
              if (element !== null) {
                QuillRef.current = element;
              }
            }}
            value={contents}
            onChange={setContents}
            modules={modules}
            placeholder="내용을 입력해주세요."
            formats={formats}
          />
          
          {/* 테이블 컨텍스트 메뉴 */}
          {tableContextMenu.visible && (
            <TableContextMenu
              style={{
                top: getMenuPosition().y,
                left: getMenuPosition().x,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ContextMenuItem onClick={addRowAbove}>↑ 위에 행 추가</ContextMenuItem>
              <ContextMenuItem onClick={addRowBelow}>↓ 아래에 행 추가</ContextMenuItem>
              <ContextMenuDivider />
              <ContextMenuItem onClick={addColLeft}>← 왼쪽에 열 추가</ContextMenuItem>
              <ContextMenuItem onClick={addColRight}>→ 오른쪽에 열 추가</ContextMenuItem>
              <ContextMenuDivider />
              <BgColorRow>
                <span>셀</span>
                <TextColorOption $color="#ffffff" onClick={() => changeCellBgColor('#ffffff')} title="흰색" />
                <TextColorOption $color="#f0f0f0" onClick={() => changeCellBgColor('#f0f0f0')} title="밝은 회색" />
                <TextColorOption $color="#e6f7ff" onClick={() => changeCellBgColor('#e6f7ff')} title="밝은 파랑" />
                <TextColorOption $color="#fff7e6" onClick={() => changeCellBgColor('#fff7e6')} title="밝은 주황" />
                <TextColorOption $color="#f6ffed" onClick={() => changeCellBgColor('#f6ffed')} title="밝은 녹색" />
                <TextColorOption $color="#fff1f0" onClick={() => changeCellBgColor('#fff1f0')} title="밝은 빨강" />
                <TextColorOption $color="#f9f0ff" onClick={() => changeCellBgColor('#f9f0ff')} title="밝은 보라" />
                <TextColorOption $color="#fffbe6" onClick={() => changeCellBgColor('#fffbe6')} title="밝은 노랑" />
              </BgColorRow>
              <BgColorRow>
                <span>행</span>
                <TextColorOption $color="#ffffff" onClick={() => changeRowBgColor('#ffffff')} title="흰색" />
                <TextColorOption $color="#f0f0f0" onClick={() => changeRowBgColor('#f0f0f0')} title="밝은 회색" />
                <TextColorOption $color="#e6f7ff" onClick={() => changeRowBgColor('#e6f7ff')} title="밝은 파랑" />
                <TextColorOption $color="#fff7e6" onClick={() => changeRowBgColor('#fff7e6')} title="밝은 주황" />
                <TextColorOption $color="#f6ffed" onClick={() => changeRowBgColor('#f6ffed')} title="밝은 녹색" />
                <TextColorOption $color="#fff1f0" onClick={() => changeRowBgColor('#fff1f0')} title="밝은 빨강" />
                <TextColorOption $color="#f9f0ff" onClick={() => changeRowBgColor('#f9f0ff')} title="밝은 보라" />
                <TextColorOption $color="#fffbe6" onClick={() => changeRowBgColor('#fffbe6')} title="밝은 노랑" />
              </BgColorRow>
              <BgColorRow>
                <span>열</span>
                <TextColorOption $color="#ffffff" onClick={() => changeColBgColor('#ffffff')} title="흰색" />
                <TextColorOption $color="#f0f0f0" onClick={() => changeColBgColor('#f0f0f0')} title="밝은 회색" />
                <TextColorOption $color="#e6f7ff" onClick={() => changeColBgColor('#e6f7ff')} title="밝은 파랑" />
                <TextColorOption $color="#fff7e6" onClick={() => changeColBgColor('#fff7e6')} title="밝은 주황" />
                <TextColorOption $color="#f6ffed" onClick={() => changeColBgColor('#f6ffed')} title="밝은 녹색" />
                <TextColorOption $color="#fff1f0" onClick={() => changeColBgColor('#fff1f0')} title="밝은 빨강" />
                <TextColorOption $color="#f9f0ff" onClick={() => changeColBgColor('#f9f0ff')} title="밝은 보라" />
                <TextColorOption $color="#fffbe6" onClick={() => changeColBgColor('#fffbe6')} title="밝은 노랑" />
              </BgColorRow>
              <ContextMenuDivider />
              <TextFormatRow onMouseDown={(e) => e.preventDefault()}>
                <FormatButton onClick={toggleCellBold} title="굵게"><b>B</b></FormatButton>
                <FormatButton onClick={toggleCellItalic} title="기울임"><i>I</i></FormatButton>
                <FormatButton onClick={toggleCellUnderline} title="밑줄"><u>U</u></FormatButton>
                <FormatButton onClick={toggleCellStrikethrough} title="취소선"><s>S</s></FormatButton>
              </TextFormatRow>
              <TextColorRow onMouseDown={(e) => e.preventDefault()}>
                <span>색상</span>
                <TextColorOption $color="#000000" onClick={() => changeCellTextColor('#000000')} title="검정" />
                <TextColorOption $color="#666666" onClick={() => changeCellTextColor('#666666')} title="회색" />
                <TextColorOption $color="#e03131" onClick={() => changeCellTextColor('#e03131')} title="빨강" />
                <TextColorOption $color="#f76707" onClick={() => changeCellTextColor('#f76707')} title="주황" />
                <TextColorOption $color="#fcc419" onClick={() => changeCellTextColor('#fcc419')} title="노랑" />
                <TextColorOption $color="#37b24d" onClick={() => changeCellTextColor('#37b24d')} title="녹색" />
                <TextColorOption $color="#1c7ed6" onClick={() => changeCellTextColor('#1c7ed6')} title="파랑" />
                <TextColorOption $color="#7950f2" onClick={() => changeCellTextColor('#7950f2')} title="보라" />
              </TextColorRow>
              <ContextMenuDivider />
              <ContextMenuItem onClick={deleteRow} $danger>행 삭제</ContextMenuItem>
              <ContextMenuItem onClick={deleteCol} $danger>열 삭제</ContextMenuItem>
              <ContextMenuDivider />
              <ContextMenuItem onClick={deleteTable} $danger>테이블 삭제</ContextMenuItem>
            </TableContextMenu>
          )}
        </EditModeContent>
      ) : (
        <Content dangerouslySetInnerHTML={markup()} savedContent={contents} />
      )}

      {(isLoading || loading) && <Loader />}
    </>
  );
}

const EditModeContent = styled.div<SContent>`
  padding: ${({ savedContent }) => (savedContent !== "" ? "40px 0" : "0")};

  p {
    font-size: 15px;
    line-height: 22.5px;
    color: ${({ theme }) => theme.color.textColor};
    font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo",
      Pretendard Variable, Roboto, "Noto Sans KR", "Segoe UI", "Malgun Gothic",
      sans-serif !important;
  }

  li {
    font-size: 15px;
    line-height: 22.5px;
    font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo",
      Pretendard Variable, Roboto, "Noto Sans KR", "Segoe UI", "Malgun Gothic",
      sans-serif !important;
  }

  strong {
    font-weight: bold;
  }

  a {
    text-decoration: underline !important;
    font-weight: bold;
    color: ${({ theme }) => theme.buttonColor.blueNormal} !important;
  }

  em {
    font-style: italic;
  }

  blockquote {
    border-left: 4px solid #ccc;
    margin-bottom: 5px;
    margin-top: 5px;
    padding-left: 16px;
  }

  img {
    max-width: 100% !important;
    cursor: auto !important;
  }
`;

const Content = styled(EditModeContent)`
  ol {
    margin-left: 24px;
    padding-left: 0;

    li {
      list-style: auto;
      line-height: 22.5px;
    }
  }

  ul {
    margin-left: 24px;
    padding-left: 0;

    li {
      list-style: disc;
      line-height: 22.5px;
    }
  }

  .ql-size-huge {
    font-size: 28px;
  }

  .ql-size-large {
    font-size: 23px;
  }

  .ql-indent-1 {
    margin-left: 20px;
    list-style: circle;
    line-height: 22.5px;
  }

  .ql-indent-2 {
    margin-left: 30px;
    list-style: square;
    line-height: 22.5px;
  }
`;



const SQuill = styled(ReactQuill)`
  .ql-editor {
    min-height: 400px;
  }

  /* 테이블 버튼 아이콘 */
  .ql-table:after {
    content: "표";
    font-size: 12px;
  }

  /* 테이블 스타일 */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 10px 0;
  }

  td {
    border: 1px solid #ccc;
    padding: 8px;
    min-width: 50px;
  }

  /* 테이블 래퍼 스타일 */
  .ql-custom-table {
    position: relative;
    display: inline-block;
    
    &:hover .table-resize-handle,
    &:hover .col-resize-handle {
      background: rgba(0, 123, 255, 0.3);
    }
  }

  .table-resize-handle:hover,
  .col-resize-handle:hover {
    background: rgba(0, 123, 255, 0.5) !important;
  }

  .qlbt-operation-menu {
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .qlbt-operation-menu-item {
    padding: 8px 16px;
    cursor: pointer;
    
    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

// 테이블 컨텍스트 메뉴 스타일
const TableContextMenu = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  min-width: 160px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  z-index: 1000;
`;

const ContextMenuItem = styled.div<{ $danger?: boolean }>`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  color: ${({ $danger }) => ($danger ? '#f42d16' : '#333')};
  
  &:hover {
    background-color: ${({ $danger }) => ($danger ? '#fff5f5' : '#f5f5f5')};
  }
`;

const ContextMenuDivider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 2px 0;
`;

// 서브메뉴 (색상 팔레트용)
const ContextMenuSubMenu = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  position: relative;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  .color-palette {
    display: none;
  }
  
  &:hover .color-palette {
    display: flex;
  }
`;

// 텍스트 서식 버튼 행
const TextFormatRow = styled.div`
  display: flex;
  padding: 6px 12px;
  gap: 4px;
  justify-content: center;
`;

// 서식 버튼
const FormatButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #007bff;
  }
`;

// 텍스트 색상 행
const TextColorRow = styled.div`
  display: flex;
  padding: 6px 12px;
  gap: 4px;
  align-items: center;
  
  span {
    font-size: 12px;
    color: #666;
    margin-right: 4px;
  }
`;

// 배경색 행
const BgColorRow = styled.div`
  display: flex;
  padding: 4px 12px;
  gap: 4px;
  align-items: center;
  
  span {
    font-size: 12px;
    color: #666;
    width: 20px;
    flex-shrink: 0;
  }
`;

// 텍스트 색상 옵션
const TextColorOption = styled.div<{ $color: string }>`
  width: 20px;
  height: 20px;
  background-color: ${({ $color }) => $color};
  border: 1px solid #ccc;
  border-radius: 3px;
  cursor: pointer;
  
  &:hover {
    border-color: #007bff;
    transform: scale(1.1);
  }
`;

// 색상 팔레트 (더 이상 사용하지 않음)
const ColorPalette = styled.div`
  position: absolute;
  left: 100%;
  top: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
  display: flex;
  flex-wrap: wrap;
  width: 140px;
  gap: 4px;
  z-index: 1001;
`;

// 색상 옵션
const ColorOption = styled.div<{ $color: string }>`
  width: 28px;
  height: 28px;
  background-color: ${({ $color }) => $color};
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;
